import { Directive, ElementRef, HostListener, inject, input } from '@angular/core'

@Directive({
  selector: '[appDigitsOnly]',
})
export class DigitsOnlyDirective {
  el = inject(ElementRef)

  inputElement

  readonly allowComma = input(true)
  readonly allowDot = input(false)
  readonly allowPrefix = input(false)
  readonly allowZeroAsTheFirstNumber = input(true)

  private readonly navigationKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'Home',
    'End',
    'ArrowLeft',
    'ArrowRight',
    'Clear',
    'Copy',
    'Paste',
  ]

  constructor() {
    const el = this.el

    this.inputElement = el.nativeElement
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(e: KeyboardEvent) {
    const target = e.target as HTMLInputElement | null
    if (!target) {
      return
    }

    const value = target.value

    const allowComma = this.allowComma()
    if (
      this.navigationKeys.includes(e.key) || // Allow: navigation keys: backspace, delete, arrows etc.
      (e.key === 'a' && (e.ctrlKey === true || e.metaKey === true)) || // Allow: Ctrl+A, Cmd+A
      (e.key === 'c' && (e.ctrlKey === true || e.metaKey === true)) || // Allow: Ctrl+C, Cmd+C
      (e.key === 'v' && (e.ctrlKey === true || e.metaKey === true)) || // Allow: Ctrl+V, Cmd+V
      (e.key === 'x' && (e.ctrlKey === true || e.metaKey === true)) || // Allow: Ctrl+X, Cmd+X
      (allowComma && e.key === ',' && !value.includes(','))
    ) {
      // let it happen, don't do anything
      return
    }

    const isDigit = /^[0-9]$/.test(e.key)
    const isComma = e.key === ',' && allowComma
    const isDot = e.key === '.' && this.allowDot()
    const isPrefix = this.allowPrefix() && /^[sSeE]$/.test(e.key)

    if (!isDigit && !isComma && !isDot && !isPrefix) {
      e.preventDefault()
    }

    // Prevent entering 0 as first number
    if (!this.allowZeroAsTheFirstNumber() && this.inputElement.value.length === 0 && e.key === '0') {
      e.preventDefault()
      return
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    event.preventDefault()
    const clipboardData = event.clipboardData
    if (!clipboardData) return

    let pastedInput = clipboardData.getData('text/plain') || ''

    const allowComma = this.allowComma()
    const allowDot = this.allowDot()
    if (this.allowPrefix()) {
      pastedInput = pastedInput.replace(/[^0-9sSeE]/g, '')
    } else if (allowDot && allowComma) {
      pastedInput = pastedInput.replace(/[^0-9,]/g, '')
    } else if (allowDot) {
      pastedInput = pastedInput.replace(/[^0-9.]/g, '')
    } else if (allowComma) {
      pastedInput = pastedInput.replace('.', ',').replace(/[^0-9,]/g, '')
    } else {
      pastedInput = pastedInput.replace(/\D/g, '')
    }

    if (!this.allowZeroAsTheFirstNumber()) {
      pastedInput = pastedInput.replace(/^0+/, '') || '0'
    }

    const input = this.el.nativeElement.querySelector('input') as HTMLInputElement
    const start = input.selectionStart || 0
    const end = input.selectionEnd || 0
    input.setRangeText(pastedInput, start, end, 'end')
    input.dispatchEvent(new Event('input', { bubbles: true }))
  }
}
